/* eslint-disable react/jsx-no-bind */
/* eslint-disable no-nested-ternary */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  MenuItem,
  Select,
  Chip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import { useMutation, useQuery } from "@apollo/client";
import { enqueueSnackbar } from "notistack";
import KuikwitButton from "src/components/shared/atoms/button";
import {
  GET_TTS_VOICE_OPTIONS,
  GET_TTS_TONE_OPTIONS,
  GET_MY_TTS_PREFERENCES,
} from "src/graphql/query/user/getTtsPreferences";
import UPDATE_MY_TTS_PREFERENCES from "src/graphql/mutation/user/updateMyTtsPreferences";
import GENERATE_TTS_AUDIO from "src/graphql/mutation/chat/generateTtsAudio";

const PREVIEW_SENTENCE =
  "Hi! This is how your voice messages will sound when sent from the CRM.";

type VoiceOpt = { id: string; label: string; gender?: string | null };
type ToneOpt = { id: string; label: string };

const VoicePreferences = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [voice, setVoice] = useState<string>("");
  const [toneId, setToneId] = useState<string>("neutral");
  const [isPlaying, setIsPlaying] = useState(false);

  const { data: voicesData } = useQuery(GET_TTS_VOICE_OPTIONS);
  const { data: tonesData } = useQuery(GET_TTS_TONE_OPTIONS);
  const { data: prefsData, loading: prefsLoading } = useQuery(
    GET_MY_TTS_PREFERENCES,
    { fetchPolicy: "network-only" },
  );

  const voices: VoiceOpt[] = useMemo(
    () => voicesData?.getTtsVoiceOptions || [],
    [voicesData],
  );
  const tones: ToneOpt[] = useMemo(
    () => tonesData?.getTtsToneOptions || [],
    [tonesData],
  );

  useEffect(() => {
    if (prefsData?.getMyTtsPreferences) {
      setVoice(prefsData.getMyTtsPreferences.voice || "");
      setToneId(prefsData.getMyTtsPreferences.toneId || "neutral");
    } else if (!voice && voices.length) {
      setVoice(voices[0].id);
    }
  }, [prefsData, voices]);

  const [updatePrefs, { loading: saving }] = useMutation(
    UPDATE_MY_TTS_PREFERENCES,
    { refetchQueries: ["getMyTtsPreferences"] },
  );
  const [generateTts, { loading: previewing }] =
    useMutation(GENERATE_TTS_AUDIO);

  const handleSave = async () => {
    if (!voice) {
      enqueueSnackbar("Please pick a voice first.", { variant: "warning" });
      return;
    }
    try {
      await updatePrefs({ variables: { voice, toneId: toneId || null } });
      enqueueSnackbar("Voice preferences saved.", { variant: "success" });
    } catch (err: any) {
      enqueueSnackbar(err?.message || "Failed to save preferences.", {
        variant: "error",
      });
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  const handlePreview = async () => {
    if (isPlaying) {
      stopAudio();
      return;
    }
    if (!voice) {
      enqueueSnackbar("Pick a voice to preview.", { variant: "warning" });
      return;
    }
    try {
      const res = await generateTts({
        variables: { text: PREVIEW_SENTENCE, voice, toneId: toneId || null },
      });
      const payload = res.data?.generateTtsAudio;
      if (!payload?.audioBase64) {
        throw new Error("No audio returned");
      }
      const src = `data:${payload.mimetype};base64,${payload.audioBase64}`;
      if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => setIsPlaying(false);
      }
      audioRef.current.src = src;
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (err: any) {
      enqueueSnackbar(err?.message || "Preview failed.", { variant: "error" });
    }
  };

  useEffect(() => () => stopAudio(), []);

  if (prefsLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}>
        <CircularProgress size={16} /> Loading voice preferences...
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={{ fontSize: 13, color: "#475569" }}>
        Pick the voice and tone used when you send a typed message as a voice
        note. This applies to WhatsApp, Facebook and Instagram chats.
      </Typography>

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        <Box sx={{ flex: "1 1 220px", minWidth: 220 }}>
          <Typography sx={{ fontSize: 12, mb: 0.5, color: "#475569" }}>
            Voice
          </Typography>
          <Select
            fullWidth
            size="small"
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
            displayEmpty
            sx={{ borderRadius: "10px", background: "#fff" }}
          >
            {voices.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                {v.label}
                {v.gender ? ` — ${v.gender}` : ""}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ flex: "2 1 360px", minWidth: 280 }}>
          <Typography sx={{ fontSize: 12, mb: 0.5, color: "#475569" }}>
            Tone preset
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {tones.map((t) => (
              <Chip
                key={t.id}
                label={t.label}
                clickable
                color={toneId === t.id ? "primary" : "default"}
                variant={toneId === t.id ? "filled" : "outlined"}
                onClick={() => setToneId(t.id)}
                size="small"
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
        <KuikwitButton
          variant="contained"
          color="primary"
          disabled={saving || !voice}
          handleClick={handleSave}
        >
          {saving ? "Saving..." : "Save preferences"}
        </KuikwitButton>
        <IconButton
          color="primary"
          onClick={handlePreview}
          disabled={previewing || !voice}
          aria-label="Preview voice"
        >
          {previewing ? (
            <CircularProgress size={18} />
          ) : isPlaying ? (
            <StopIcon />
          ) : (
            <PlayArrowIcon />
          )}
        </IconButton>
        <Typography sx={{ fontSize: 12, color: "#64748b" }}>
          {isPlaying ? "Playing preview..." : "Preview sample line"}
        </Typography>
      </Box>
    </Box>
  );
};

export default VoicePreferences;
